import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api, type DebugUserProfile } from '../api-client';
import './DebugUserPickerComponent.css';

interface DebugUserPickerProps {
  profiles: DebugUserProfile[];
  returnTo?: string | null;
}

export function DebugUserPicker({ profiles, returnTo }: DebugUserPickerProps): JSX.Element {
  const navigate = useNavigate();
  const [loading, setLoading] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const handleLogin = async (profile: DebugUserProfile): Promise<void> => {
    setLoading(profile.id);
    setError(null);

    const result = await api.debugLogin(profile.id);

    if (result.success) {
      // Redirect to returnTo path or default to home
      navigate(returnTo ?? '/');
    } else {
      setError(result.error.message);
      setLoading(null);
    }
  };

  const getGroupBadgeClass = (group: string): string => {
    const groupClasses: Record<string, string> = {
      admin: 'badge-admin',
      sales: 'badge-sales',
      'sales-engineering': 'badge-sales-eng',
      support: 'badge-support',
      'professional-services': 'badge-ps',
      marketing: 'badge-marketing',
      devops: 'badge-devops',
      tam: 'badge-tam',
      'product-engineering': 'badge-product-eng',
      engineering: 'badge-engineering',
    };
    return groupClasses[group] ?? 'badge-default';
  };

  return (
    <div className="debug-picker">
      <div className="debug-picker-warning">
        Debug Mode - For Local Testing Only
      </div>

      {error && <div className="debug-picker-error">{error}</div>}

      <div className="debug-picker-profiles">
        {profiles.map((profile) => (
          <button
            key={profile.id}
            className="debug-profile-btn"
            onClick={() => handleLogin(profile)}
            disabled={loading !== null}
          >
            <div className="debug-profile-info">
              <span className="debug-profile-name">{profile.displayName}</span>
              <span className="debug-profile-desc">{profile.description}</span>
            </div>
            <div className="debug-profile-groups">
              {profile.groups.map((group) => (
                <span
                  key={group}
                  className={`debug-profile-badge ${getGroupBadgeClass(group)}`}
                >
                  {group}
                </span>
              ))}
            </div>
            {loading === profile.id && (
              <span className="debug-profile-loading">...</span>
            )}
          </button>
        ))}
      </div>
    </div>
  );
}
